import { z } from 'zod';

import { jsonRequest } from './shared';
import { ApiResponseError } from './response';

export const LoyaltyProgramStatusSchema = z.enum(['enabled', 'disabled', 'preview']);
export const LoyaltyEarnModeSchema = z.enum(['per_spend', 'per_order', 'hybrid']);
export const LoyaltyRoundingModeSchema = z.enum(['floor', 'ceil', 'nearest']);

export type LoyaltyProgramStatus = z.infer<typeof LoyaltyProgramStatusSchema>;
export type LoyaltyEarnMode = z.infer<typeof LoyaltyEarnModeSchema>;
export type LoyaltyRoundingMode = z.infer<typeof LoyaltyRoundingModeSchema>;

export const EffectiveLoyaltyProgramSchema = z
  .object({
    programId: z.string(),
    overrideId: z.string().nullable(),
    status: LoyaltyProgramStatusSchema,
    earnMode: LoyaltyEarnModeSchema,
    pointsPerCurrency: z.number(),
    currencyUnit: z.number(),
    pointsPerOrder: z.number().nullable(),
    roundingMode: LoyaltyRoundingModeSchema,
    notes: z.string().nullable(),
    redemptionEnabled: z.boolean(),
    redemptionPointsPerUnit: z.number(),
    redemptionCurrencyUnit: z.number(),
    redemptionMinPoints: z.number(),
    redemptionMaxPercent: z.number(),
  })
  .passthrough();

export type EffectiveLoyaltyProgram = z.infer<typeof EffectiveLoyaltyProgramSchema>;

const LoyaltyProgramSchema = z
  .object({
    id: z.string(),
    tenantId: z.string(),
    status: LoyaltyProgramStatusSchema,
    earnMode: LoyaltyEarnModeSchema,
    pointsPerCurrency: z.number(),
    currencyUnit: z.number(),
    pointsPerOrder: z.number().nullable(),
    roundingMode: LoyaltyRoundingModeSchema,
    notes: z.string().nullable(),
    redemptionEnabled: z.boolean(),
    redemptionPointsPerUnit: z.number(),
    redemptionCurrencyUnit: z.number(),
    redemptionMinPoints: z.number(),
    redemptionMaxPercent: z.number(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .passthrough();

const LoyaltyProgramOverrideSchema = z
  .object({
    id: z.string(),
    tenantId: z.string(),
    programId: z.string(),
    locationId: z.string(),
    status: LoyaltyProgramStatusSchema.nullable(),
    earnMode: LoyaltyEarnModeSchema.nullable(),
    pointsPerCurrency: z.number().nullable(),
    currencyUnit: z.number().nullable(),
    pointsPerOrder: z.number().nullable(),
    roundingMode: LoyaltyRoundingModeSchema.nullable(),
    notes: z.string().nullable(),
    redemptionEnabled: z.boolean().nullable(),
    redemptionPointsPerUnit: z.number().nullable(),
    redemptionCurrencyUnit: z.number().nullable(),
    redemptionMinPoints: z.number().nullable(),
    redemptionMaxPercent: z.number().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .passthrough();

export type LoyaltyProgramRecord = z.infer<typeof LoyaltyProgramSchema>;
export type LoyaltyProgramOverrideRecord = z.infer<typeof LoyaltyProgramOverrideSchema>;

const TenantProgramResponseSchema = z
  .object({
    program: LoyaltyProgramSchema,
    overrides: z.array(LoyaltyProgramOverrideSchema),
    effective: EffectiveLoyaltyProgramSchema,
  })
  .passthrough();

const LocationProgramResponseSchema = z
  .object({
    effective: EffectiveLoyaltyProgramSchema,
  })
  .passthrough();

type FetchProgramOptions = {
  locationId?: string | null;
  signal?: AbortSignal;
};

const handleLoyaltyRequest = async <T>(
  path: string,
  schema: z.ZodType<T>,
  signal?: AbortSignal,
): Promise<T | null> => {
  try {
    return await jsonRequest<T>(path, { signal, schema });
  } catch (error) {
    if (error instanceof ApiResponseError && error.status === 404) {
      return null;
    }
    throw error;
  }
};

export const fetchEffectiveLoyaltyProgram = async ({
  locationId,
  signal,
}: FetchProgramOptions): Promise<EffectiveLoyaltyProgram | null> => {
  if (locationId) {
    const response = await handleLoyaltyRequest(
      `/settings/loyalty/program/locations/${locationId}`,
      LocationProgramResponseSchema,
      signal,
    );
    return response ? response.effective : null;
  }

  const response = await handleLoyaltyRequest(
    '/settings/loyalty/program',
    TenantProgramResponseSchema,
    signal,
  );
  return response ? response.effective : null;
};

const createDefaultLoyaltyProgram = (): LoyaltyProgramRecord => {
  const timestamp = new Date().toISOString();
  return {
    id: 'loyalty-program-default',
    tenantId: 'placeholder-tenant',
    status: 'disabled',
    earnMode: 'per_spend',
    pointsPerCurrency: 1,
    currencyUnit: 100,
    pointsPerOrder: null,
    roundingMode: 'nearest',
    notes: null,
    redemptionEnabled: false,
    redemptionPointsPerUnit: 100,
    redemptionCurrencyUnit: 100,
    redemptionMinPoints: 0,
    redemptionMaxPercent: 100,
    createdAt: timestamp,
    updatedAt: timestamp,
  };
};

const createDefaultEffectiveProgram = (program: LoyaltyProgramRecord): EffectiveLoyaltyProgram => ({
  programId: program.id,
  overrideId: null,
  status: program.status,
  earnMode: program.earnMode,
  pointsPerCurrency: program.pointsPerCurrency,
  currencyUnit: program.currencyUnit,
  pointsPerOrder: program.pointsPerOrder,
  roundingMode: program.roundingMode,
  notes: program.notes,
  redemptionEnabled: program.redemptionEnabled,
  redemptionPointsPerUnit: program.redemptionPointsPerUnit,
  redemptionCurrencyUnit: program.redemptionCurrencyUnit,
  redemptionMinPoints: program.redemptionMinPoints,
  redemptionMaxPercent: program.redemptionMaxPercent,
});

export const LOYALTY_PROGRAM_QUERY_KEY = ['settings', 'loyalty-program'] as const;

export type LoyaltyProgramManagementResponse = z.infer<typeof TenantProgramResponseSchema>;

export type LoyaltyProgramUpdateInput = {
  status?: LoyaltyProgramStatus;
  earnMode?: LoyaltyEarnMode;
  pointsPerCurrency?: number | null;
  currencyUnit?: number | null;
  pointsPerOrder?: number | null;
  roundingMode?: LoyaltyRoundingMode;
  notes?: string | null;
  redemptionEnabled?: boolean;
  redemptionPointsPerUnit?: number | null;
  redemptionCurrencyUnit?: number | null;
  redemptionMinPoints?: number | null;
  redemptionMaxPercent?: number | null;
};

const buildProgramPayload = (input: LoyaltyProgramUpdateInput) => {
  const payload: Record<string, unknown> = {};

  if (input.status) {
    payload.status = input.status;
  }
  if (input.earnMode) {
    payload.earnMode = input.earnMode;
  }
  if (input.roundingMode) {
    payload.roundingMode = input.roundingMode;
  }
  if (input.pointsPerCurrency !== undefined) {
    payload.pointsPerCurrency = input.pointsPerCurrency;
  }
  if (input.currencyUnit !== undefined) {
    payload.currencyUnit = input.currencyUnit;
  }
  if (input.pointsPerOrder !== undefined) {
    payload.pointsPerOrder = input.pointsPerOrder;
  }
  if (input.notes !== undefined) {
    payload.notes = input.notes;
  }

  if (input.redemptionEnabled !== undefined) {
    payload.redemptionEnabled = input.redemptionEnabled;
  }
  if (input.redemptionPointsPerUnit !== undefined) {
    payload.redemptionPointsPerUnit = input.redemptionPointsPerUnit;
  }
  if (input.redemptionCurrencyUnit !== undefined) {
    payload.redemptionCurrencyUnit = input.redemptionCurrencyUnit;
  }
  if (input.redemptionMinPoints !== undefined) {
    payload.redemptionMinPoints = input.redemptionMinPoints;
  }
  if (input.redemptionMaxPercent !== undefined) {
    payload.redemptionMaxPercent = input.redemptionMaxPercent;
  }

  return payload;
};

export const fetchLoyaltyProgramConfig = async (signal?: AbortSignal): Promise<LoyaltyProgramManagementResponse> => {
  try {
    return await jsonRequest('/settings/loyalty/program', {
      signal,
      schema: TenantProgramResponseSchema,
    });
  } catch (error) {
    if (error instanceof ApiResponseError && error.status === 404) {
      const program = createDefaultLoyaltyProgram();
      return {
        program,
        effective: createDefaultEffectiveProgram(program),
        overrides: [],
      };
    }
    throw error;
  }
};

export const updateLoyaltyProgram = async (
  payload: LoyaltyProgramUpdateInput,
): Promise<LoyaltyProgramRecord> => {
  const response = await jsonRequest<{ program: LoyaltyProgramRecord }>('/settings/loyalty/program', {
    method: 'PUT',
    body: buildProgramPayload(payload),
    schema: z.object({ program: LoyaltyProgramSchema }),
  });
  return response.program;
};

export const upsertLoyaltyProgramOverride = async (
  locationId: string,
  payload: LoyaltyProgramUpdateInput,
): Promise<LoyaltyProgramOverrideRecord> => {
  const response = await jsonRequest<{ override: LoyaltyProgramOverrideRecord }>(
    `/settings/loyalty/program/overrides/${encodeURIComponent(locationId)}`,
    {
      method: 'PUT',
      body: buildProgramPayload(payload),
      schema: z.object({ override: LoyaltyProgramOverrideSchema }),
    },
  );

  return response.override;
};

export const deleteLoyaltyProgramOverride = async (locationId: string): Promise<void> => {
  await jsonRequest(`/settings/loyalty/program/overrides/${encodeURIComponent(locationId)}`, {
    method: 'DELETE',
    schema: z.object({}).passthrough(),
  });
};
